import { useRef, useState } from 'react';
import { applyOrder } from '../../application/columns/use-columns';
import { STRUCTURAL_IDS } from '../../defaults';
import type { ResolvedColumn, TableSettings } from '../../domain';
import { buildColumnGroups } from '../../domain/schema/grouping';
import type { ColumnGroup } from '../../domain/schema/grouping';
import { moveItem } from '../../domain/shared/move-item';
import { Icon } from '../shared/Icon';
import { useClickOutside } from '../shared/use-click-outside';
import './ColumnChooser.scss';

export interface ColumnChooserProps<T> {
    all: ResolvedColumn<T>[];
    hidden: string[];
    settings: TableSettings;
}
type Group<T> = ColumnGroup<T>;
interface DragSlot {
    scope: number | 'top';
    index: number;
}
interface RowProps {
    label: string;
    checked: boolean;
    disabled?: boolean;
    indeterminate?: boolean;
    indent?: boolean;
    dragging?: boolean;
    dropEdge?: 'before' | 'after';
    chevron?: { open: boolean; onToggle: () => void };
    onCheck: (checked: boolean) => void;
    onMove: (dir: -1 | 1) => void;
    onDragStart: () => void;
    onDragOver: () => void;
    onDragEnd: () => void;
    onDrop: () => void;
}

const indeterminateRef =
    (value: boolean) =>
    (el: HTMLInputElement | null): void => {
        if (el) el.indeterminate = value;
    };

function ColumnRow(props: RowProps): JSX.Element {
    const {
        label,
        checked,
        disabled,
        indeterminate,
        indent,
        dragging,
        dropEdge,
        chevron,
        onCheck,
        onMove,
    } = props;
    const { onDragStart, onDragOver, onDragEnd, onDrop } = props;
    const cls = [
        'ics-table__column-option',
        indent ? 'ics-table__column-option--leaf' : '',
        dragging ? 'ics-table__column-option--dragging' : '',
        dropEdge ? `ics-table__column-option--drop-${dropEdge}` : '',
    ]
        .filter(Boolean)
        .join(' ');
    const onKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            onMove(-1);
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            onMove(1);
        }
    };
    return (
        <div
            className={cls}
            onDrop={onDrop}
            onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                onDragOver();
            }}
        >
            {chevron ? (
                <button
                    type="button"
                    className="ics-table__column-chevron"
                    aria-expanded={chevron.open}
                    aria-label={`${chevron.open ? 'Collapse' : 'Expand'} ${label} group`}
                    onClick={chevron.onToggle}
                >
                    <Icon
                        name="keyboard_arrow_down"
                        className={
                            chevron.open
                                ? ''
                                : 'ics-table__column-chevron--closed'
                        }
                    />
                </button>
            ) : (
                <span
                    className="ics-table__column-chevron-spacer"
                    aria-hidden="true"
                />
            )}
            <input
                type="checkbox"
                className="ics-table__column-checkbox"
                checked={checked}
                disabled={disabled}
                title={
                    disabled
                        ? 'At least one column must stay visible'
                        : undefined
                }
                aria-label={label}
                onChange={(e) => {
                    onCheck(e.target.checked);
                }}
                ref={
                    indeterminate === undefined
                        ? undefined
                        : indeterminateRef(indeterminate)
                }
            />
            <button
                type="button"
                className="ics-table__column-drag"
                aria-label={`Reorder ${label}`}
                draggable
                onKeyDown={onKeyDown}
                onDragEnd={onDragEnd}
                onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', label);
                    onDragStart();
                }}
            >
                <span
                    aria-hidden="true"
                    className="guardian-icon guardian-icon--small"
                >
                    drag_indicator
                </span>
            </button>
            <span className="ics-table__column-label">{label}</span>
        </div>
    );
}

export function ColumnChooser<T>(props: ColumnChooserProps<T>): JSX.Element {
    const { all, hidden, settings } = props;
    const [open, setOpen] = useState(false);
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
    const dragRef = useRef<DragSlot | null>(null);
    const [drag, setDrag] = useState<DragSlot | null>(null);
    const [over, setOver] = useState<DragSlot | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useClickOutside(
        containerRef,
        () => {
            setOpen(false);
        },
        open,
    );
    const columns = applyOrder(
        all.filter((c) => !STRUCTURAL_IDS.includes(c.id)),
        settings.layout.columnOrder,
    );
    const visibleCount = columns.length - hidden.length;
    const blocks = buildColumnGroups(columns);

    const toggle = (col: ResolvedColumn<T>, checked: boolean): void => {
        if (!checked && visibleCount <= 1) return;
        settings.setHidden(col.id, !checked);
    };
    const toggleGroup = (block: Group<T>): void => {
        const visibleColumns = block.columns.filter(
            (c) => !hidden.includes(c.id),
        );
        if (visibleColumns.length !== block.columns.length) {
            block.columns.forEach((c) => {
                toggle(c, true);
            });
            return;
        }
        let remaining = visibleCount;
        [...visibleColumns].reverse().forEach((c) => {
            if (remaining <= 1) return;
            toggle(c, false);
            remaining -= 1;
        });
    };
    const commitOrder = (next: Group<T>[]): void => {
        settings.setOrder(next.flatMap((b) => b.columns.map((c) => c.id)));
    };
    const move = (scope: number | 'top', from: number, to: number): void => {
        if (scope === 'top') {
            const next = moveItem(blocks, from, to);
            if (next !== blocks) commitOrder(next);
            return;
        }
        const cols = blocks[scope].columns;
        const next = moveItem(cols, from, to);
        if (next !== cols)
            commitOrder(
                blocks.map((b, i) =>
                    i === scope ? { ...b, columns: next } : b,
                ),
            );
    };
    const startDrag = (slot: DragSlot): void => {
        dragRef.current = slot;
        setDrag(slot);
    };
    const endDrag = (): void => {
        dragRef.current = null;
        setDrag(null);
        setOver(null);
    };
    const drop = (scope: number | 'top', index: number): void => {
        const from = dragRef.current;
        endDrag();
        if (from && from.scope === scope && from.index !== index)
            move(scope, from.index, index);
    };
    const dropEdge = (
        scope: number | 'top',
        index: number,
    ): 'before' | 'after' | undefined => {
        if (!drag || !over || drag.scope !== scope || over.scope !== scope)
            return undefined;
        if (over.index !== index || drag.index === index) return undefined;
        return drag.index < index ? 'after' : 'before';
    };
    const toggleCollapse = (key: string): void => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <div className="ics-table__column-chooser" ref={containerRef}>
            <button
                type="button"
                className="ics-table__column-chooser-tab"
                aria-expanded={open}
                aria-label="Columns"
                onClick={() => {
                    setOpen((was) => !was);
                }}
            >
                <Icon
                    name="settings"
                    className="ics-table__column-chooser-icon"
                />
                Columns
            </button>
            {open ? (
                <div className="ics-table__column-chooser-panel">
                    {blocks.map((block, idx) => {
                        const isGroup = block.header !== undefined;
                        const key =
                            block.header ?? `col:${block.columns[0].id}`;
                        const label = block.header ?? block.columns[0].header;
                        const visible = block.columns.filter(
                            (c) => !hidden.includes(c.id),
                        ).length;
                        const onCheck = isGroup
                            ? () => {
                                  toggleGroup(block);
                              }
                            : (c: boolean) => {
                                  toggle(block.columns[0], c);
                              };
                        return (
                            <div key={key} className="ics-table__column-group">
                                <ColumnRow
                                    label={label}
                                    checked={visible > 0}
                                    disabled={
                                        visibleCount <= 1 &&
                                        visible === block.columns.length
                                    }
                                    onCheck={onCheck}
                                    indeterminate={Boolean(
                                        isGroup &&
                                            visible > 0 &&
                                            visible < block.columns.length,
                                    )}
                                    chevron={
                                        isGroup
                                            ? {
                                                  open: !collapsed.has(key),
                                                  onToggle: () => {
                                                      toggleCollapse(key);
                                                  },
                                              }
                                            : undefined
                                    }
                                    onMove={(dir) => {
                                        move('top', idx, idx + dir);
                                    }}
                                    dragging={
                                        drag?.scope === 'top' &&
                                        drag.index === idx
                                    }
                                    dropEdge={dropEdge('top', idx)}
                                    onDragStart={() => {
                                        startDrag({ scope: 'top', index: idx });
                                    }}
                                    onDragOver={() => {
                                        setOver({ scope: 'top', index: idx });
                                    }}
                                    onDragEnd={endDrag}
                                    onDrop={() => {
                                        drop('top', idx);
                                    }}
                                />
                                {isGroup && !collapsed.has(key)
                                    ? block.columns.map((col, ci) => (
                                          <ColumnRow
                                              key={col.id}
                                              indent
                                              label={col.header}
                                              checked={!hidden.includes(col.id)}
                                              disabled={
                                                  visibleCount <= 1 &&
                                                  !hidden.includes(col.id)
                                              }
                                              onCheck={(c) => {
                                                  toggle(col, c);
                                              }}
                                              onMove={(dir) => {
                                                  move(idx, ci, ci + dir);
                                              }}
                                              dragging={
                                                  drag?.scope === idx &&
                                                  drag.index === ci
                                              }
                                              dropEdge={dropEdge(idx, ci)}
                                              onDragStart={() => {
                                                  startDrag({
                                                      scope: idx,
                                                      index: ci,
                                                  });
                                              }}
                                              onDragOver={() => {
                                                  setOver({
                                                      scope: idx,
                                                      index: ci,
                                                  });
                                              }}
                                              onDragEnd={endDrag}
                                              onDrop={() => {
                                                  drop(idx, ci);
                                              }}
                                          />
                                      ))
                                    : null}
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        className="ics-table__reset-button"
                        onClick={settings.reset}
                    >
                        Reset to default
                    </button>
                </div>
            ) : null}
        </div>
    );
}
